import { defineStore } from 'pinia'
import type { List } from '../../types/content'
import { useEntityStore } from '../composables/useEntityStore'

export const useListsStore = defineStore('lists', () => {
  const {
    items: lists,
    sortBy,
    sortOrder,
    sortItems,
    sortedItems: sortedLists,
    getById,
    add,
    update,
    remove,
    removeBulk,
    restore,
    restoreBulk,
    moveToContext,
    toggleFavourite,
    toggleFavouriteBulk,
    setSortBy,
    toggleSortOrder,
    loadSortPreferences
  } = useEntityStore<List>({
    table: 'lists',
    sortPreferencePrefix: 'lists',
    sortStrategies: {
      title: (a: List, b: List) => b.title.localeCompare(a.title) as number
    }
  })

  const sortedFavouriteLists = computed(() => {
    return sortItems(lists.value.filter((n) => n.favourite))
  })

  const sortedRegularLists = computed(() => {
    return sortItems(lists.value.filter((n) => !n.favourite))
  })

  /**
   * Retrieves a list by its ID, ensuring labels array is initialized.
   * @param id - The list ID
   * @returns The list or null if not found
   */
  async function getListById(id: string): Promise<List | null> {
    const list = await getById(id)
    if (!list) return null
    list.labels = list.labels ?? []
    return list
  }

  /**
   * Adds a new list with the given title and the current context's ID.
   * @param title - The title of the new list.
   * @returns The created list or null on failure.
   */
  async function addNewList(title: string): Promise<List | null> {
    return add({ title, items: [], labels: [] } as Partial<List>)
  }

  /**
   * Updates a list in the database.
   * @param updates - An object containing the fields to update.
   * @returns A promise that resolves when the list is updated.
   */
  async function updateList(
    updates: Partial<List & { id: string }>
  ): Promise<void> {
    return update(updates)
  }

  /**
   * Removes a list from the database.
   * @param id - The ID of the list to remove.
   */
  async function removeList(id: string): Promise<void> {
    return remove(id)
  }

  /**
   * Removes multiple lists.
   * @param ids - Array of list IDs to remove.
   */
  async function removeListsInBulk(ids: string[]): Promise<void> {
    return removeBulk(ids)
  }

  /**
   * Restores a previously deleted list.
   * @param id - The ID of the list to restore.
   */
  async function restoreList(id: string): Promise<void> {
    return restore(id)
  }

  /**
   * Restores multiple previously deleted lists.
   * @param ids - Array of list IDs to restore.
   */
  async function restoreListsInBulk(ids: string[]): Promise<void> {
    return restoreBulk(ids)
  }

  /**
   * Moves lists to a different context.
   * @param ids - Array of list IDs to move.
   * @param contextId - Target context ID.
   */
  async function moveListsToAnotherContext(
    ids: string[],
    contextId: string
  ): Promise<void> {
    return moveToContext(ids, contextId)
  }

  /**
   * Toggles the favourite status of a list.
   * @param id - The list ID.
   */
  async function toggleFavouriteList(id: string): Promise<void> {
    return toggleFavourite(id)
  }

  /**
   * Toggles the favourite status of multiple lists.
   * @param ids - Array of list IDs.
   */
  async function toggleFavouriteListsInBulk(ids: string[]): Promise<void> {
    return toggleFavouriteBulk(ids)
  }

  return {
    lists,
    sortBy,
    sortOrder,
    sortedLists,
    sortedFavouriteLists,
    sortedRegularLists,
    getListById,
    addNewList,
    updateList,
    removeList,
    removeListsInBulk,
    moveListsToAnotherContext,
    restoreList,
    restoreListsInBulk,
    setSortBy,
    toggleSortOrder,
    loadSortPreferences,
    toggleFavouriteList,
    toggleFavouriteListsInBulk
  }
})
